import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Users, CheckCircle, AlertCircle, Loader, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { showFriendlyError } from '../utils/friendlyErrors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function JoinFamilyPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    fetchInviteDetails();
  }, [token]);

  const fetchInviteDetails = async () => {
    try {
      const response = await axios.get(`${API}/family/invite/${token}`);
      setInviteDetails(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invite link');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinFamily = async () => {
    if (!isAuthenticated) {
      // Redirect to signup with return URL
      navigate(`/signup?redirect=/join-family/${token}`);
      return;
    }

    setJoining(true);
    try {
      const response = await axios.post(
        `${API}/family/accept-invite/${token}`,
        {},
        {
          ...getAuthHeader(),
          withCredentials: true
        }
      );

      if (response.data.success) {
        toast.success('You have joined the family!');
        navigate('/app/dashboard');
      }
    } catch (err) {
      showFriendlyError(err, { fallback: 'Failed to join family' });
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a1729]">
      <Navbar />
      
      <div className="flex items-center justify-center min-h-[80vh] px-4">
        <div className="glass-card p-8 w-full max-w-md text-center">
          {loading ? (
            <div className="py-12">
              <Loader className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
              <p className="text-slate-300">Loading invite details...</p>
            </div>
          ) : error ? (
            <div className="py-8">
              <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">Invalid Invite</h2>
              <p className="text-slate-300 mb-6">{error}</p>
              <Link to="/">
                <Button className="bg-slate-700 hover:bg-slate-600">
                  Go Home
                </Button>
              </Link>
            </div>
          ) : inviteDetails ? (
            <div className="py-4">
              <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-blue-400" />
              </div>
              
              <h2 className="text-xl font-semibold text-white mb-2">
                Family Invitation
              </h2>
              
              <p className="text-slate-300 mb-6">
                <span className="text-white font-medium">{inviteDetails.parent_name}</span> has invited you to join their family on Agentic FuturePath AI
              </p>
              
              <div className="bg-slate-800/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-slate-300">Invited as</p>
                <p className="text-lg font-semibold text-white">{inviteDetails.child_name}</p>
              </div>

              {isAuthenticated ? (
                <Button
                  onClick={handleJoinFamily}
                  disabled={joining}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {joining ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Joining...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Accept & Join Family
                    </>
                  )}
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-300">
                    Create an account or log in to accept this invitation
                  </p>
                  <Button
                    onClick={() => navigate(`/signup?redirect=/join-family/${token}`)}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    Create Account
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    onClick={() => navigate(`/login?redirect=/join-family/${token}`)}
                    variant="outline"
                    className="w-full border-slate-700"
                  >
                    I already have an account
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
